import React, { useState, useRef, useContext, useCallback, useEffect } from "react"
import { LoaderMedium } from "../../../components/Loaders"
import ChatMessage from "./ChatMessage"
import trcss from "../../../styles/Telerecovery.module.css"
import { InputTextarea } from 'primereact/inputtextarea'
import { Button } from 'primereact/button'
import { Tooltip } from "primereact/tooltip"
import { ReactComponent as HistoryIcon } from "../resources/history_icon.svg"
import { HubConnectionBuilder } from "@microsoft/signalr"
import { UserContext } from "../../../context/userContext"
import { telerecoveryApiUrl } from "../../../config/microservicesUrls"
import { noteApiMarkAllAsReadByParticipantPut } from "../../../components/notifications/notificationsApi"
import { trApiGetSMSHistory, trApiSendSMSToParticipant, trApiSMSSetRead } from "../telerecoveryApi"
import { groupMessagesByDate, getActualUTCTime, InactiveStatuses, getTimeAgo } from "../Utils"
import { FileUpload } from 'primereact/fileupload'
import { Image } from 'primereact/image'
import classNames from 'classnames'
import { Toast } from 'primereact/toast'
import isParticipantActive from "../../../helpers/isParticipantActive"

export default function ChatComponent({ staffPhoneNumber, participant, onNewMessage, isOkayToText }) {
  const [messages, setMessages] = useState([])
  const [newSMSText, setNewSMSText] = useState("")
  const [loadingMessages, setLoadingMessages] = useState(true)
  const [sendingMessage, setSendingMessage] = useState(false)
  const [connection, setConnection] = useState(null)
  const [mmsFile, setMmsFile] = useState(null)
  const messagesEndRef = useRef(null)
  const userCtx = useContext(UserContext)
  const { user } = userCtx.session
  const toast = useRef(null)

  const isParticipantInactive = !isParticipantActive(participant.membership_status || userCtx.participant_membership_status)

  const uploadHandler = (event) => {
    const file = event.files[0]
    setMmsFile(file)
    event.options.clear()
  }

  const getMessageHistory = useCallback(() => {
    setLoadingMessages(true)
    trApiGetSMSHistory(userCtx.tokenTelerecovery, staffPhoneNumber, participant.phone, participant.id, user.organization_id)
      .then(
        (response) => {
          setMessages(Array.from(response))
          setLoadingMessages(false)
          scrollToBottom()
        },
        (err) => {
          console.log(err)
          if (err.request.status === 400) {
            toast.current.show({
              severity: "warn",
              summary: "Inconsistency Detected",
              detail: err.request.response,
              life: 5000,
            })
          }
          setLoadingMessages(false)
        }
      )
      .catch(() => {
        setLoadingMessages(false)
      })
  }, [
    staffPhoneNumber,
    participant
  ])

  const markConversationRead = useCallback(() => {
    let data = {
      participantNumber: participant.phone,
      staffNumber: staffPhoneNumber,
    }
    trApiSMSSetRead(userCtx.tokenTelerecovery, data).then((resp) => {
    }, [])
  }, [
    participant,
    staffPhoneNumber,
    userCtx.tokenTelerecovery
  ])

  useEffect(() => {
    noteApiMarkAllAsReadByParticipantPut(
      userCtx.tokenNotification,
      userCtx,
      participant.id,
      undefined,
      "incoming_text"
    ).then(() => userCtx.setNotificationRefresh(true))
    getMessageHistory()
    markConversationRead()
  }, [getMessageHistory, markConversationRead])

  useEffect(() => {
    const _connection = new HubConnectionBuilder()
      .withUrl(telerecoveryApiUrl() + "/notificationHub")
      .withAutomaticReconnect()
      .build()
    setConnection(_connection)
  }, [])

  const checkIncomingMessage = useCallback((from, to, body) => {
    if (
      from !== staffPhoneNumber
    ) {

      if (from === participant.phone) {

        let newMsg = {
          dateUpdated: getActualUTCTime(),
          body: body,
          to: to,
          from: from,
          sentByParticipant: true,
        }

        if (onNewMessage) {
          onNewMessage(newMsg)
        }

        noteApiMarkAllAsReadByParticipantPut(
          userCtx.tokenNotification,
          userCtx,
          participant.id,
          undefined,
          "incoming_text"
        ).then(() => userCtx.setNotificationRefresh(true))

        setMessages((oldArray) => [...oldArray, newMsg])
        scrollToBottom()
      }
    }
  }, [staffPhoneNumber, participant])

  useEffect(() => {
    if (connection) {
      // Clean up previous listeners to avoid multiple subscriptions
      connection.off("IncomingMessage")

      // Only attempt to start the connection if it's disconnected
      if (connection.state === "Disconnected") {
        connection.start()
          .then((result) => {
            connection.on("IncomingMessage", (from, to, body) => {
              checkIncomingMessage(from, to, body)
            })
          })
          .catch((e) => console.log("Connection failed: ", e))
      } else {
        // Re-subscribe to the event
        connection.on("IncomingMessage", (from, to, body) => {
          checkIncomingMessage(from, to, body)
        })
      }
      // Cleanup function to unregister event handlers
      return () => {
        if (connection) {
          connection.off("IncomingMessage")
        }
      }
    }
  }, [connection, checkIncomingMessage])

  const scrollToBottom = () => {
    messagesEndRef?.current?.scrollIntoView({ behavior: "smooth" })
  }

  const sendMessage = () => {
    if ((newSMSText === "" && mmsFile === null) || sendingMessage) {
      return
    }

    setSendingMessage(true)

    const smsData = new FormData()
    smsData.append('from', staffPhoneNumber)
    smsData.append('to', participant.phone)
    smsData.append('body', newSMSText)
    smsData.append('peerId', user.staff_id)
    smsData.append('caseId', participant.id)
    smsData.append('soarOrganizationId', participant.organization_id)
    if (mmsFile) {
      smsData.append('file', mmsFile)
    }

    trApiSendSMSToParticipant(userCtx.tokenTelerecovery, smsData)
      .then(
        (response) => {
          let body = smsData.get('body')
          if (response.fileUrl && response.fileUrl !== "") {
            body += `\n${response.fileUrl}`
          }
          let newMsg = {
            dateUpdated: getActualUTCTime(),
            body: body,
            to: participant.phone,
            from: staffPhoneNumber,
            sentByParticipant: false,
          }
          if (onNewMessage) {
            onNewMessage(newMsg)
          }
          setMessages((oldArray) => [...oldArray, newMsg])
          setNewSMSText("")
          setMmsFile(null)
          scrollToBottom()
        },
        (err) => {
          if (err.request.status === 400) {
            let newMsg = {
              dateUpdated: getActualUTCTime(),
              body: err.request?.response,
              to: staffPhoneNumber,
              from: participant.phone,
              sentByParticipant: true,
            }
            if (onNewMessage) {
              onNewMessage(newMsg)
            }
            setMessages((oldArray) => [...oldArray, newMsg])
            setNewSMSText("")
            scrollToBottom()
          } else {
            console.log(err)
          }
        }
      ).catch((err) => {
        console.log(err)
      })
      .finally(() => {
        setSendingMessage(false)
      })
  }

  return (
    <div
      className={trcss.chatWindow}
    >
      <Toast ref={toast} />
      <div className={trcss.chatWindowHeader}>
        <div>
          <span className={trcss.userInitials}>{participant.initials}</span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "15px" }}>
          {messages && messages.length > 0 && (
            <p style={{ fontSize: "11px", alignSelf: "center" }}>
              {`Last message: ${getTimeAgo(messages[messages.length - 1].dateUpdated)}`}
            </p>
          )}
          <i
            className="pi pi-info-circle"
            style={{ fontSize: '20px', color: "#8467ad" }}
            id="info_icon" />
          <Tooltip target="#info_icon" className="custom-tooltip" position='top' autoHide={false}>
            <span>
              SMS/Email notifications for new chat messages can be turned on/off in <p className={trcss.tooltipLink}
                onClick={() => userCtx.setShowNotificationsSettings(true)}
              >Manage Notifications</p>
            </span>
          </Tooltip>
          <i
            id="history_icon" >
            <HistoryIcon />
          </i>
          <Tooltip target="#history_icon" className="custom-tooltip" position='bottom'>
            <span>
              Only messages within last 90 days are displayed
            </span>
          </Tooltip>
        </div>
      </div>
      <div className={trcss.chatHistoryContainer}>
        {loadingMessages === true && <LoaderMedium />}
        {(!messages || messages.length === 0) && !loadingMessages && (
          <span className={trcss.convEmptyMsg}>
            This conversation is empty.
          </span>
        )}
        <ul className={trcss.chatWindowList}>
          {!loadingMessages &&
            messages &&
            groupMessagesByDate(messages, "from").map((grupedMessage, i) => (
              <li key={"msg-" + i}>
                <ChatMessage
                  messages={grupedMessage.messages}
                  fromName={participant.name}
                  fromInitials={participant.initials}
                ></ChatMessage>
              </li>
            ))}
        </ul>
        <div ref={messagesEndRef}></div>
      </div>
      {mmsFile && mmsFile.objectURL &&
        <div className={trcss.mms_preview_container}>
          <Image
            src={mmsFile.objectURL}
            alt={mmsFile.name}
            width="150"
            preview
            className={trcss.mms}
          />
          <Button
            className={'p-button-rounded p-button-icon-only p-button-sm'}
            icon="pi pi-times"
            onClick={() => setMmsFile(null)}
          />
        </div>
      }
      <div className={trcss.chatWindowFooter}>
        <InputTextarea
          placeholder="Type your message"
          autoResize
          className={trcss.newMessageBox}
          value={newSMSText}
          onChange={(e) => setNewSMSText(e.target.value)}
          rows={1}
          disabled={isOkayToText === false || isParticipantInactive}
        />
        <FileUpload
          className={classNames(trcss.mms_file_upload, isOkayToText === false && trcss.mms_file_upload_disabled)}
          accept="image/jpeg,image/jpg,image/gif,image/png"
          maxFileSize={4000000}
          auto
          customUpload
          uploadHandler={uploadHandler}
          chooseOptions={{
            className: 'p-button-icon-only',
            icon: 'pi pi-paperclip',
            iconPos: 'left'
          }}
          id={'fileUpload'}
          disabled={isOkayToText === false || isParticipantInactive}
        />
        <Tooltip target="#fileUpload" className="custom-tooltip" position='top' autoHide={false}>
          <span>Attach an image to your message</span>
        </Tooltip>
        <Button
          className={trcss.sendBtn}
          onClick={sendMessage}
          loading={sendingMessage}
          disabled={isOkayToText === false || isParticipantInactive || sendingMessage}
        />
      </div>
      {!loadingMessages && isOkayToText === false &&
        <div className={trcss.consent_message}>
          <i className="pi pi-exclamation-triangle" style={{ fontSize: "24px", color: "#873CAA" }} />
          <div>
            <p style={{ fontWeight: "bold" }}>The participant has not consented to being contacted via SMS</p>
            <p>Please update their contact information if you would like to be able to send messages</p>
          </div>
        </div>
      }
    </div>
  )
}